import { FilterByLocation as SharedFilterByLocation } from '@components/shared/FilterByLocation';
import { buildSearchRef } from '@components/shared/Search/helpers';
import {
	effectiveCategoryCodeAtom,
	isMultiOrNoCategoryUIAtom,
} from '@pages/VendorsSearch/containers/FilterPills/atoms';
import { PRESERVE_CATEGORY_QUERY_PARAM } from '@pages/VendorsSearch/hooks/classifier-sync-helpers';
import { useAppSelector } from '@redux/hooks';
import { TextBody } from '@tkww/orion-web-ds-text';
import { formatResultsCount } from '@utils/formatResultsCount';
import { useAtomValue } from 'jotai';
import React, { useCallback, useState } from 'react';
import { useLocation } from 'react-router';
import urlSafe from '../../../../utils/urlHelpers';
import { useLocationHeaderTracking } from './hooks/use-location-header-tracking';
import Styles from './styles.scss';

interface LocationHeaderProps {
	/** Total number of vendor results */
	totalVendors: number;
	/** Category or SEO filter text (e.g., "Wedding Venues" or "Affordable Wedding Venues") */
	headerText: string;
	/** Semantic search string, if any (e.g., "romantic wedding") */
	semanticSearchString?: string;
}

/**
 * LocationHeader component for VendorsSearch page
 * Shows location selection in the results header
 * Format examples:
 *   - "120 results for Wedding Venues in San Diego, CA"
 *   - "120 results for "romantic wedding" for Wedding Venues in San Diego, CA"
 *   - "120 results for "romantic wedding" in San Diego, CA" (no category in cross-category search)
 * Where "120 results" is bold and "San Diego, CA" is the interactive location selector
 */
export const LocationHeader = ({
	totalVendors,
	headerText,
	semanticSearchString,
}: LocationHeaderProps) => {
	const location = useAppSelector((state) => state.location);
	const categories = useAppSelector((state) => state.categories);
	const categoryCode = useAppSelector((state) => state.category.code);
	// effectiveCategoryCode reflects Jotai filter selection immediately when a
	// category pill filter is applied — before the async Redux update from
	// WrappedVendorsSearch's CATEGORY_LOADED dispatch. This prevents a stale
	// Redux categoryCode from being used in buildSearchRef when the user
	// changes location right after applying a single cross-category filter.
	const effectiveCategoryCode = useAtomValue(effectiveCategoryCodeAtom);
	const { search } = useLocation();
	const [isOpen, setIsOpen] = useState(false);
	const hasLocation = Boolean(location.city && location.stateCode);
	const isMultiOrNoCategory = useAtomValue(isMultiOrNoCategoryUIAtom);
	const showCategoryInTitle = !(isMultiOrNoCategory || !!semanticSearchString);
	const { handleLocationSelection, handleDropdownOpen, handleInputChange } =
		useLocationHeaderTracking(categoryCode);

	const handleLocationChange = useCallback(
		(newLocation: { city: string; stateCode: string }) => {
			// Track before navigation so the event isn't dropped by the page unload
			handleLocationSelection(newLocation);

			if (isMultiOrNoCategory) {
				// On Cross Category with no category or multiple categories selected, navigate to
				// the location-only Cross Category URL. Using effectiveCategoryCode here would apply
				// a stale Redux category code and incorrectly redirect to a single-category
				// page.
				const newLocationSlug = `${urlSafe(newLocation.city)}-${urlSafe(newLocation.stateCode)}`;
				const currentParams = new URLSearchParams(search);
				currentParams.delete('page');
				currentParams.delete('offset');

				// URLSearchParams decodes '+' as space, so multi-category values like
				// "wedding-venues+djs" are decoded as "wedding-venues djs". Only keep
				// category when there are 2+ tokens (multi-category); remove it when
				// it is absent or represents a single (potentially stale) category.
				const categoryParam = currentParams.get('category');
				const isMultiCategory =
					categoryParam !== null &&
					categoryParam.split(' ').filter(Boolean).length > 1;
				if (!isMultiCategory) {
					currentParams.delete('category');
				}

				// Same search, only the location changed — the (possibly empty)
				// category selection above already reflects an explicit choice for
				// this query, so tell the destination page's classifier sync not to
				// auto-select over it (GS-1468). Only set when the classifier is
				// actually active — otherwise nothing ever consumes/strips this
				// marker and it leaks into the URL permanently.
				if (semanticSearchString) {
					currentParams.set(PRESERVE_CATEGORY_QUERY_PARAM, '1');
				}

				const qs = currentParams.toString();
				window.location.href = `/marketplace/${newLocationSlug}${qs ? `?${qs}` : ''}`;
				return;
			}

			const searchRef = buildSearchRef(categories, effectiveCategoryCode, {
				city: newLocation.city,
				stateCode: newLocation.stateCode,
			});

			if (searchRef) {
				const origin = window.location.origin || 'https://theknot.com';
				const url = new URL(searchRef, origin);
				const currentParams = new URLSearchParams(search);

				for (const [key, value] of currentParams.entries()) {
					if (!url.searchParams.has(key)) {
						url.searchParams.set(key, value);
					}
				}

				url.searchParams.delete('page');
				url.searchParams.delete('offset');

				// Same search, only the location changed — this single-category URL
				// already reflects an explicit choice for this query, so tell the
				// destination page's classifier sync not to auto-select over it
				// (GS-1468). Only set when the classifier is actually active —
				// otherwise nothing ever consumes/strips this marker and it leaks
				// into the URL permanently.
				if (semanticSearchString) {
					url.searchParams.set(PRESERVE_CATEGORY_QUERY_PARAM, '1');
				}

				window.location.href = [url.pathname, url.search].join('');
			}
		},
		[
			handleLocationSelection,
			categories,
			effectiveCategoryCode,
			search,
			isMultiOrNoCategory,
			semanticSearchString,
		],
	);

	const handleToggle = useCallback(() => {
		if (!isOpen) handleDropdownOpen();
		setIsOpen((prev) => !prev);
	}, [isOpen, handleDropdownOpen]);

	const handleClose = useCallback(() => {
		setIsOpen(false);
	}, []);

	const resultsCount = formatResultsCount(totalVendors);
	const resultsLabel = totalVendors === 1 ? 'result' : 'results';
	const locationLabel = hasLocation
		? ` in ${location.city}, ${location.stateCode}`
		: '';

	const searchLabel = semanticSearchString
		? ` for \u201C${semanticSearchString}\u201D`
		: '';
	const categoryLabel = showCategoryInTitle ? ` for ${headerText}` : '';
	const ariaLabel = `${resultsCount} ${resultsLabel}${searchLabel}${categoryLabel}${locationLabel}`;

	return (
		<div className={Styles.locationHeader}>
			<h1 className={Styles.visuallyHidden}>{ariaLabel}</h1>
			<TextBody size="xl" className={Styles.resultsText} as="div">
				<strong>
					{resultsCount} {resultsLabel}
				</strong>{' '}
				{semanticSearchString && <>for &ldquo;{semanticSearchString}&rdquo;</>}
				{showCategoryInTitle && (
					<>
						{semanticSearchString ? ' ' : ''}
						for {headerText}
					</>
				)}
				{hasLocation ? ' in ' : ''}
				{location.city && location.stateCode && (
					<SharedFilterByLocation
						className={Styles.filterInline}
						label=""
						selectedLocation={{
							city: location.city,
							stateCode: location.stateCode,
						}}
						onLocationChange={handleLocationChange}
						buttonText="Search"
						isOpen={isOpen}
						onToggle={handleToggle}
						onClose={handleClose}
						onInputChange={handleInputChange}
						testId="vendors-search-location-header"
						textVariant="body"
					/>
				)}
			</TextBody>
		</div>
	);
};
